import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useFieldArrayOf } from '@framework/form/form/relations/useFieldsArrayOf.ts';
import { RichTextButton } from '@framework/form/inputs/rich-text/menu/RichTextMenuButton.tsx';
import {
  type IRichTextInputProps,
  RichTextInput,
} from '@framework/form/inputs/rich-text/RichTextInput.tsx';
import { StyledMenuBarWrapper } from '@framework/form/inputs/rich-text/style/StyledMenuBarWrapper.tsx';
import { StyledRteEditor } from '@framework/form/inputs/rich-text/style/StyledRteEditor.tsx';
import { type Editor } from '@tiptap/react';
import { Button, Popconfirm, Space, Tooltip } from 'antd';
import clx from 'classnames';
import { useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { ExpressionsEditorStyles } from '../../schema/expressions/compute/input/ComputeExpressionMinimalBaseInput.tsx';
import { ComputeExpressionReferenceNode } from '../../schema/expressions/compute/input/nodes/ComputeExpressionReferenceNode.tsx';
import { computeExpressionReferenceSuggestion } from '../../schema/expressions/compute/input/reference-suggestions/computeExpressionReferenceSuggestion.tsx';
import { useTreeReferencesContext } from '../../tree/references/TreeReferencesContext.tsx';
import { ConditionsEditorInput } from '../expressions-editor/conditions/ConditionsEditorInput.tsx';
import { type IAdvancedRichTextItem } from './IAdvancedRichText.ts';
import { RichTextItemFooterDisplay } from './item-footer/RichTextItemFooterDisplay.tsx';
import { PlaceholderRichTextButton } from './menu-bar/PlaceholderRichTextButton.tsx';
import { ReferenceRichTextButton } from './menu-bar/ReferenceRichTextButton.tsx';
import { RichTextAdvancedExtensions } from './RichTextAdvancedExtensions.tsx';
import { RichTextFooterRow } from './RichTextFooterRow.tsx';
import { RichTextInfoInputButton } from './RichTextInfoInputButton.tsx';
import { RichTextUsageInfoPopover } from './RichTextUsageInfoPopover.tsx';

const RichTextAdvancedInputStyles = styled.div`
  .rte-container {
    &:not(.first-item) {
      ${StyledMenuBarWrapper} {
        &.rte-menu-wrapper-lg,
        .rte-menu-wrapper-sm {
          border-top-color: transparent;
          border-radius: 0;
        }
      }
    }
    &:not(.last-item) {
      ${StyledRteEditor} {
        &.rte-input-lg,
        .rte-input-sm {
          border-radius: 0;
        }
      }
    }
    ${StyledRteEditor} {
      &.rte-input-lg,
      .rte-input-sm {
        border-bottom-right-radius: 0;
      }
    }
  }
`;

const AddButtonStyles = styled(Button)`
  &&& {
    border-top-left-radius: 0;
    border-top-right-radius: 0;
    margin-top: -1px;
  }
`;

export interface IRichTextAdvancedInputProps extends IRichTextInputProps {}

export function RichTextAdvancedInput(props: IRichTextAdvancedInputProps) {
  const { name, extensions } = props;
  const { references, placeholderReferences } = useTreeReferencesContext();

  const { fields, append, remove } = useFieldArrayOf<IAdvancedRichTextItem>({
    name,
  });

  const referenceExtensions = useMemo(() => {
    const suggestions = computeExpressionReferenceSuggestion(references ?? []);
    return [
      ...RichTextAdvancedExtensions.filter(
        extension => extension.name !== 'compute-expression-reference',
      ),
      // Aggiungo l'estensione per il suggerimento
      ComputeExpressionReferenceNode.configure({
        suggestion: suggestions,
      }),
    ];
  }, [references]);

  const allExtensions = useMemo(
    () => [...referenceExtensions, ...(extensions ?? [])],
    [referenceExtensions, extensions],
  );

  const extendedMenu = useCallback(
    (editor: Editor | null) => {
      return (
        <>
          {placeholderReferences.length > 0 ? (
            <PlaceholderRichTextButton
              editor={editor}
              references={placeholderReferences}
            />
          ) : (
            <ReferenceRichTextButton editor={editor} references={references} />
          )}
          {props.extendedMenu?.(editor)}
        </>
      );
    },
    [placeholderReferences, references, props.extendedMenu],
  );

  return (
    <RichTextAdvancedInputStyles className="RichTextAdvancedInputStyles">
      <Space size={0} direction="vertical" style={{ width: '100%' }}>
        {fields?.map((field, i) => {
          const fieldPrefix = `${props.name}.${i}`;

          return (
            <div
              key={field.id}
              className={clx('rte-container', {
                'first-item': i === 0,
                'last-item': i === fields.length - 1,
              })}
            >
              <ExpressionsEditorStyles>
                <RichTextInput
                  {...props}
                  extensions={allExtensions}
                  extendedMenu={extendedMenu}
                  name={`${fieldPrefix}.text`}
                  rteItemTypeFieldName={`${fieldPrefix}.type`}
                  rows={2}
                  extra={
                    <Space>
                      {props.extra}
                      <RichTextInfoInputButton name={`${fieldPrefix}.type`} />
                      <Tooltip
                        title="Imposta regole di visibilità"
                        placement="left"
                      >
                        <ConditionsEditorInput
                          size="small"
                          name={`${fieldPrefix}.rule`}
                          references={references}
                        />
                      </Tooltip>
                      <Popconfirm
                        placement="topRight"
                        title="Vuoi eliminare il testo?"
                        okText={
                          <span>
                            <DeleteOutlined /> Elimina
                          </span>
                        }
                        description="Verranno eliminate anche le regole di visibilità associate."
                        okButtonProps={{
                          danger: true,
                          type: 'primary',
                          disabled: fields.length < 2,
                        }}
                        onConfirm={() => {
                          remove(i);
                        }}
                      >
                        <RichTextButton
                          icon={<DeleteOutlined />}
                          title="Elimina testo"
                          danger
                        />
                      </Popconfirm>
                    </Space>
                  }
                  footer={<RichTextItemFooterDisplay name={`${fieldPrefix}`} />}
                />
              </ExpressionsEditorStyles>
            </div>
          );
        })}
        <RichTextFooterRow extra={<RichTextUsageInfoPopover />}>
          <Space>
            <AddButtonStyles
              icon={<PlusOutlined aria-hidden="true" />}
              onClick={() => {
                append({
                  type: 'text',
                  text: '',
                });
              }}
              title="Aggiungi Testo"
            >
              <span>Aggiungi Testo</span>
            </AddButtonStyles>
          </Space>
        </RichTextFooterRow>
      </Space>
    </RichTextAdvancedInputStyles>
  );
}
